// src/services/subscriptionService.js
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db, COLLECTIONS } from './firebase';

// Add to COLLECTIONS in firebase.js
export const SUBSCRIPTION_COLLECTIONS = {
  SUBSCRIPTIONS: 'taskdata-subscriptions',
  PAYMENTS: 'taskdata-payments',
  PLANS: 'taskdata-plans'
};

export class SubscriptionService {
  // Get available plans
  static async getPlans() {
    const earlyBirdEnd = new Date('2025-06-30');
    const isEarlyBird = new Date() < earlyBirdEnd;
    
    const plans = [
      {
        id: 'individual-term',
        type: 'individual',
        name: 'Individual Educator - Term',
        description: 'Perfect for individual teachers',
        duration: 'term',
        durationDays: 120,
        price: 180,
        features: [
          'Unlimited assessment tasks',
          'Performance analytics',
          'Submission tracking',
          'Basic reports',
          'Cloud storage (2GB)'
        ]
      },
      {
        id: 'individual-annual',
        type: 'individual',
        name: 'Individual Educator - Annual',
        description: 'Best value for individual teachers',
        duration: 'annual',
        durationDays: 365,
        price: 600,
        originalPrice: isEarlyBird ? 600 : null,
        discountedPrice: isEarlyBird ? 300 : null,
        savings: isEarlyBird ? 300 : 120,
        popular: true,
        features: [
          'Everything in Term plan',
          'Priority support',
          'Advanced analytics',
          'Collaboration tools',
          'Cloud storage (10GB)',
          'Early bird 50% off!'
        ]
      },
      {
        id: 'school-term',
        type: 'school',
        name: 'School Plan - Term',
        description: 'For schools and institutions',
        duration: 'term',
        durationDays: 120,
        price: 90,
        priceUnit: 'per educator',
        minUsers: 5,
        features: [
          'All Individual features',
          'Multi-educator management',
          'School-wide analytics',
          'Admin dashboard',
          'Bulk user management'
        ]
      },
      {
        id: 'school-annual',
        type: 'school',
        name: 'School Plan - Annual',
        description: 'Best value for schools',
        duration: 'annual',
        durationDays: 365,
        price: 240,
        originalPrice: isEarlyBird ? 240 : null,
        discountedPrice: isEarlyBird ? 120 : null,
        priceUnit: 'per educator',
        minUsers: 5,
        popular: true,
        features: [
          'Everything in School Term',
          'Advanced reporting suite',
          'Custom branding',
          'API access',
          'Dedicated support',
          'Early bird 50% off!'
        ]
      },
      {
        id: 'ai-reports',
        type: 'addon',
        name: 'AI Reports Add-on',
        description: 'Intelligent performance insights',
        duration: 'annual',
        durationDays: 365,
        price: 60,
        priceUnit: 'per learner',
        features: [
          'AI-powered analysis',
          'Personalized recommendations',
          'Learning gap identification',
          'Up to 40 reports per subject',
          'Predictive insights',
          'Parent-friendly reports'
        ]
      }
    ];

    return plans;
  }

  // Create subscription
  static async createSubscription(subscriptionData) {
    try {
      const subscriptionRef = await addDoc(collection(db, SUBSCRIPTION_COLLECTIONS.SUBSCRIPTIONS), {
        ...subscriptionData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return subscriptionRef.id;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Get user's current subscription
  static async getUserSubscription(userId) {
    try {
      const q = query(
        collection(db, SUBSCRIPTION_COLLECTIONS.SUBSCRIPTIONS),
        where('userId', '==', userId),
        where('status', 'in', ['active', 'pending'])
      );
      
      const querySnapshot = await getDocs(q);
      const subscriptions = [];
      
      querySnapshot.forEach(doc => {
        subscriptions.push({ id: doc.id, ...doc.data() });
      });
      
      return subscriptions;
    } catch (error) {
      console.error('Error getting user subscription:', error);
      throw error;
    }
  }

  // Update subscription status
  static async updateSubscriptionStatus(subscriptionId, status, paymentData = {}) {
    try {
      const subscriptionRef = doc(db, SUBSCRIPTION_COLLECTIONS.SUBSCRIPTIONS, subscriptionId);
      
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        ...paymentData
      };

      if (status === 'active') {
        updateData.activatedAt = serverTimestamp();
        updateData.expiresAt = new Date(Date.now() + (updateData.durationDays * 24 * 60 * 60 * 1000));
      }

      await updateDoc(subscriptionRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  // Record payment
  static async recordPayment(paymentData) {
    try {
      const paymentRef = await addDoc(collection(db, SUBSCRIPTION_COLLECTIONS.PAYMENTS), {
        ...paymentData,
        createdAt: serverTimestamp()
      });

      return paymentRef.id;
    } catch (error) {
      console.error('Error recording payment:', error);
      throw error;
    }
  }

  // Check if user has access to AI reports
  static async hasAIReportsAccess(userId) {
    try {
      const subscriptions = await this.getUserSubscription(userId);
      
      return subscriptions.some(sub => 
        sub.status === 'active' && 
        (sub.planId === 'ai-reports' || sub.planId.includes('annual'))
      );
    } catch (error) {
      console.error('Error checking AI reports access:', error);
      return false;
    }
  }

  // Get AI reports usage
  static async getAIReportsUsage(userId, planId) {
    try {
      const q = query(
        collection(db, 'taskdata-ai-reports'),
        where('userId', '==', userId),
        where('planId', '==', planId),
        where('createdAt', '>=', new Date(Date.now() - (365 * 24 * 60 * 60 * 1000)))
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting AI reports usage:', error);
      return 0;
    }
  }
}